/**
 * EmpathyScreen - Motivational Interstitial Component
 *
 * Displays reassuring/motivational content between quiz sections.
 * Based on PuraU's empathy screen pattern.
 */

import React, { useCallback, useEffect, useRef } from 'react';
import ProgressTimer from './ProgressTimer';

interface EmpathyScreenProps {
  screenId: string;
  headline: string;
  body: string;
  caption?: string | undefined;
  description: string;
  onContinue: () => void;
  buttonLabel?: string;
  isTapToContinue?: boolean;
  onBack?: (() => void) | undefined;
}

const EmpathyScreen: React.FC<EmpathyScreenProps> = ({
  screenId,
  headline,
  body,
  caption,
  description,
  onContinue,
  buttonLabel,
  isTapToContinue = false,
  onBack
}) => {
  const hasContinuedRef = useRef(false);
  useEffect(() => {
    hasContinuedRef.current = false;
  }, [screenId]);

  // Why it matters: tap and timer completion can happen on the same screen; only one should advance the quiz.
  const continueOnce = useCallback(() => {
    if (hasContinuedRef.current) return;

    hasContinuedRef.current = true;
    onContinue();
  }, [onContinue]);

  // Why it matters: tappable screens must also continue with Enter/Space for keyboard users.
  const handleTapKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (!isTapToContinue) return;

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      continueOnce();
    }
  };

  return (
    <div
      className={`empathy-screen ${isTapToContinue ? 'empathy-screen-tappable' : ''}`}
      onClick={isTapToContinue ? continueOnce : undefined}
      onKeyDown={isTapToContinue ? handleTapKeyDown : undefined}
      role={isTapToContinue ? 'button' : undefined}
      tabIndex={isTapToContinue ? 0 : undefined}
    >
      <div className="empathy-screen-content">
        {caption && <span className='caption'>{caption}</span>}
        <h1 className="empathy-headline">{headline}</h1>
        {description && <p className="empathy-body description">{description}</p>}
        {body && <p className="empathy-body subtitle">{body}</p>}
      </div>
      {isTapToContinue && (
        <ProgressTimer
          duration={2500}
          resetKey={screenId}
          onComplete={continueOnce}
        />
      )}
      <div className="empathy-screen-navigation" onClick={(event) => event.stopPropagation()} onKeyDown={(event) => event.stopPropagation()}>
        {!isTapToContinue && (
          <button
            className="quiz-btn quiz-btn-primary"
            onClick={continueOnce}
          >
            {buttonLabel || 'CONTINUE'}
          </button>
        )}
        {onBack && (
          <button
            className="quiz-btn quiz-btn-back"
            onClick={onBack}
          >
            ← BACK
          </button>
        )}
      </div>
    </div>
  );
};

export default EmpathyScreen;
